import { type MouseEvent, useRef, useState } from "react"

import {
  Bar,
  ChartArea,
  ChartLegend,
  ChartTooltip,
  ChartTooltipLabel,
  ChartTooltipValue,
  LegendItem,
  LegendSwatch,
  WeekGroup,
  WeekLabel,
  WeeklyChart,
  WeeklyTrendPanel,
  WeeklyTrendTitle,
  YAxis,
} from "./styled"

interface SeriesConfig<T> {
  key: keyof T
  label: string
  color: string
}

type HoveredWeeklyValue = {
  week: string
  label: string
  value: number
  x: number
  y: number
}

interface WeeklyTrendChartProps<T extends { week: string }> {
  data: T[]
  series: Array<SeriesConfig<T>>
}

const MIN_WEEKLY_AXIS_MAX = 100
const WEEKLY_AXIS_SMALL_STEP = 20
const WEEKLY_AXIS_LARGE_STEP = 100

const getWeeklyTrendAxisStep = (value: number) =>
  value <= MIN_WEEKLY_AXIS_MAX ? WEEKLY_AXIS_SMALL_STEP : WEEKLY_AXIS_LARGE_STEP

export const getWeeklyTrendAxisMax = <T extends { week: string }>(data: T[], series: Array<SeriesConfig<T>>) =>
  Math.ceil(
    Math.max(
      MIN_WEEKLY_AXIS_MAX,
      ...data.flatMap(item =>
        series.map(seriesItem => Number(item[seriesItem.key])).filter(value => Number.isFinite(value)),
      ),
    ) / WEEKLY_AXIS_LARGE_STEP,
  ) * WEEKLY_AXIS_LARGE_STEP

const getYAxisTicks = (axisMax: number) =>
  Array.from(
    { length: Math.floor(axisMax / getWeeklyTrendAxisStep(axisMax)) + 1 },
    (_, index) => axisMax - getWeeklyTrendAxisStep(axisMax) * index,
  )

const getBarHeight = (value: number, axisMax: number) => Math.max(2, Math.min(100, (value / axisMax) * 100))

export const WeeklyTrendChart = <T extends { week: string }>({ data, series }: WeeklyTrendChartProps<T>) => {
  const [hoveredValue, setHoveredValue] = useState<HoveredWeeklyValue | null>(null)
  const chartAreaRef = useRef<HTMLDivElement | null>(null)
  const axisMax = getWeeklyTrendAxisMax(data, series)
  const yAxisTicks = getYAxisTicks(axisMax)

  const handleBarHover = (event: MouseEvent<HTMLDivElement>, item: T, seriesItem: SeriesConfig<T>) => {
    const chartRect = chartAreaRef.current?.getBoundingClientRect()
    const value = Number(item[seriesItem.key])

    setHoveredValue({
      week: item.week,
      label: seriesItem.label,
      value,
      x: chartRect ? event.clientX - chartRect.left : event.clientX,
      y: chartRect ? event.clientY - chartRect.top : event.clientY,
    })
  }

  return (
    <WeeklyTrendPanel>
      <WeeklyTrendTitle>Weekly Trend</WeeklyTrendTitle>
      <WeeklyChart>
        <YAxis>
          {yAxisTicks.map(tick => (
            <span key={tick}>{tick}</span>
          ))}
        </YAxis>
        <ChartArea ref={chartAreaRef} $columns={data.length} onMouseLeave={() => setHoveredValue(null)}>
          {hoveredValue ? (
            <ChartTooltip $x={hoveredValue.x} $y={hoveredValue.y} aria-label="weekly trend tooltip">
              <ChartTooltipLabel>
                {hoveredValue.week} · {hoveredValue.label}
              </ChartTooltipLabel>
              <ChartTooltipValue>{hoveredValue.value.toLocaleString()}</ChartTooltipValue>
            </ChartTooltip>
          ) : null}
          {data.map(item => (
            <WeekGroup key={item.week}>
              {series.map(seriesItem => (
                <Bar
                  key={String(seriesItem.key)}
                  data-testid={`weekly-trend-bar-${item.week}-${String(seriesItem.key)}`}
                  $color={seriesItem.color}
                  $height={getBarHeight(Number(item[seriesItem.key]), axisMax)}
                  $seriesCount={series.length}
                  onMouseEnter={event => handleBarHover(event, item, seriesItem)}
                  onMouseMove={event => handleBarHover(event, item, seriesItem)}
                />
              ))}
              <WeekLabel>{item.week}</WeekLabel>
            </WeekGroup>
          ))}
        </ChartArea>
      </WeeklyChart>
      <ChartLegend>
        {series.map(seriesItem => (
          <LegendItem key={String(seriesItem.key)}>
            <LegendSwatch $color={seriesItem.color} />
            {seriesItem.label}
          </LegendItem>
        ))}
      </ChartLegend>
    </WeeklyTrendPanel>
  )
}
